import React from "react";
import Icon from "@mdi/react";
import {
	mdiHospitalBuilding,
	mdiDoctor,
	mdiAccountHeart,
	mdiChartLine,
	mdiStethoscope,
	mdiFileDocumentEdit,
	mdiHeadCog,
	mdiShieldCheck,
	mdiChevronRight,
	mdiClipboardPulse,
	mdiCalendarClock,
	mdiPill,
	mdiChatProcessing,
	mdiMenu,
	mdiMedicalBag,
	mdiVideo,
} from "@mdi/js";
import { Routes, Route } from "react-router-dom";
import { Button } from "@mui/material";
import { MainLayout, MobileLayoutProvider, useMobileLayout } from "layouts";
import { navigate } from "shared/utils";
import { useAccount } from "shared/hooks/useAccount";
import type { MedicalProfileType } from "shared/model/AccountModel";
import { MedDoctorPage } from "./MedDoctorPage";
import { MedPatientPage } from "./MedPatientPage";
import { MedDashboardPage } from "./MedDashboardPage";
import { TeleconsultationPage } from "./TeleconsultationPage";
import styles from "./styles.module.scss";

// ── Helpers ───────────────────────────────────────────────────────

function getGreeting(): string {
	const h = new Date().getHours();
	if (h < 12) return "Bom dia";
	if (h < 18) return "Boa tarde";
	return "Boa noite";
}

function getFirstName(account: { displayName?: string; username?: string } | null): string {
	const raw = account?.displayName || account?.username || "";
	const first = raw.split(" ")[0];
	return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

// ── Landing / Welcome Page ────────────────────────────────────────

const MedLandingPage: React.FC = () => {
	const account = useAccount();
	const firstName = getFirstName(account);
	const { isMobile, openDrawer } = useMobileLayout();
	const profile = (account?.settings?.medicalProfile as MedicalProfileType) || null;
	const isDoctor = profile === "doctor" || profile === "both";
	const isPatient = profile === "patient" || profile === "both";

	return (
		<MainLayout>
			<div className={styles.welcomePage}>
				{/* ── Mobile Menu Button ──────────────── */}
				{isMobile && (
					<button className={styles.welcomeMenuBtn} onClick={openDrawer} aria-label="Menu">
						<Icon path={mdiMenu} size={1.1} />
						<span>Menu</span>
					</button>
				)}

				{/* ── Hero ────────────────────────────── */}
				<section className={styles.welcomeHero}>
					<div className={styles.welcomeHeroContent}>
						<span className={styles.welcomeBadge}>
							<Icon path={mdiMedicalBag} size={0.7} />
							Telemedicina com Inteligência Artificial
						</span>

						<h1>
							{firstName
								? <>{getGreeting()}, <strong>{isDoctor ? `Dr(a). ${firstName}` : firstName}</strong>! 🩺</>
								: <>Bem-vindo(a) ao <strong>Med.IA</strong>! 🩺</>
							}
						</h1>

						<p className={styles.welcomeSubtitle}>
							O <strong>Med.IA</strong> é a plataforma de telemedicina inteligente que conecta médicos e pacientes
							através de agentes de IA avançados. Transcrição automática de consultas, copiloto clínico com
							sugestões baseadas em evidências, triagem inteligente e acompanhamento contínuo — tudo em um só lugar.
						</p>
					</div>

					<div className={styles.welcomeHeroVisual}>
						<Icon path={mdiStethoscope} size={5} color="var(--mui-palette-primary-main)" />
					</div>
				</section>

				{/* ── Highlights ──────────────────────── */}
				<section className={styles.welcomeHighlights}>
					<div className={styles.highlightItem}>
						<div className={styles.highlightIcon} style={{ background: "rgba(0, 59, 113, 0.08)", color: "#003B71" }}>
							<Icon path={mdiFileDocumentEdit} size={1.2} />
						</div>
						<h3>IA Redatora</h3>
						<p>Transcrição automática em tempo real e estruturação inteligente do prontuário.</p>
					</div>
					<div className={styles.highlightItem}>
						<div className={styles.highlightIcon} style={{ background: "rgba(0, 150, 167, 0.08)", color: "#0096A7" }}>
							<Icon path={mdiHeadCog} size={1.2} />
						</div>
						<h3>IA Conselheira</h3>
						<p>Copiloto clínico que entrega sugestões baseadas em evidências científicas.</p>
					</div>
					<div className={styles.highlightItem}>
						<div className={styles.highlightIcon} style={{ background: "rgba(0, 119, 182, 0.08)", color: "#0077B6" }}>
							<Icon path={mdiAccountHeart} size={1.2} />
						</div>
						<h3>IA Enfermeira</h3>
						<p>Coleta informações pré-consulta e realiza acompanhamento pós-consulta.</p>
					</div>
					<div className={styles.highlightItem}>
						<div className={styles.highlightIcon} style={{ background: "rgba(108, 99, 255, 0.08)", color: "#6C63FF" }}>
							<Icon path={mdiChartLine} size={1.2} />
						</div>
						<h3>IA Business Intelligence</h3>
						<p>KPIs clínicos, adesão a protocolos e métricas de qualidade em tempo real.</p>
					</div>
				</section>

				{/* ── Section Title ───────────────────── */}
				<div className={styles.welcomeSectionTitle}>
					<h2>Acesse as áreas da plataforma</h2>
					<p>Escolha seu perfil para começar a utilizar as ferramentas médicas.</p>
				</div>

				{/* ── Cards ───────────────────────────── */}
				<div className={styles.landingCards}>
					{/* Card Médico */}
					{isDoctor && (
					<div className={styles.landingCard} onClick={() => navigate("/medical/doctor")}>
						<div className={styles.landingCardIcon} style={{ backgroundColor: "rgba(0, 59, 113, 0.1)", color: "#003B71" }}>
							<Icon path={mdiDoctor} size={2} />
						</div>
						<h2>Área do Médico</h2>
						<p>Atenda pacientes com auxílio de IA, transcreva consultas e receba sugestões clínicas em tempo real.</p>
						<ul className={styles.landingFeatureList}>
							<li><Icon path={mdiChatProcessing} size={0.65} /> Chat com agentes IA especializados</li>
							<li><Icon path={mdiFileDocumentEdit} size={0.65} /> Prontuário eletrônico inteligente</li>
							<li><Icon path={mdiClipboardPulse} size={0.65} /> Sugestões clínicas baseadas em evidências</li>
							<li><Icon path={mdiCalendarClock} size={0.65} /> Gestão de consultas e agenda</li>
						</ul>
						<div className={styles.landingCardAction}>
							<span>Acessar</span>
							<Icon path={mdiChevronRight} size={0.8} />
						</div>
					</div>
					)}

					{/* Card Paciente */}
					{isPatient && (
					<div className={styles.landingCard} onClick={() => navigate("/medical/patient")}>
						<div className={styles.landingCardIcon} style={{ backgroundColor: "rgba(0, 150, 167, 0.1)", color: "#0096A7" }}>
							<Icon path={mdiAccountHeart} size={2} />
						</div>
						<h2>Área do Paciente</h2>
						<p>Acompanhe sua saúde, converse com a IA enfermeira e acesse seu histórico médico completo.</p>
						<ul className={styles.landingFeatureList}>
							<li><Icon path={mdiAccountHeart} size={0.65} /> IA Enfermeira 24h para acompanhamento</li>
							<li><Icon path={mdiClipboardPulse} size={0.65} /> Histórico de consultas e exames</li>
							<li><Icon path={mdiPill} size={0.65} /> Receitas e medicamentos ativos</li>
							<li><Icon path={mdiCalendarClock} size={0.65} /> Agendamento de consultas</li>
						</ul>
						<div className={styles.landingCardAction}>
							<span>Acessar</span>
							<Icon path={mdiChevronRight} size={0.8} />
						</div>
					</div>
					)}

					{/* Card Dashboard — só médicos */}
					{isDoctor && (
					<div className={styles.landingCard} onClick={() => navigate("/medical/dashboard")}>
						<div className={styles.landingCardIcon} style={{ backgroundColor: "rgba(0, 119, 182, 0.1)", color: "#0077B6" }}>
							<Icon path={mdiChartLine} size={2} />
						</div>
						<h2>Dashboard Clínico</h2>
						<p>Visualize KPIs clínicos, adesão a protocolos e métricas de qualidade do atendimento.</p>
						<ul className={styles.landingFeatureList}>
							<li><Icon path={mdiChartLine} size={0.65} /> KPIs clínicos e operacionais</li>
							<li><Icon path={mdiShieldCheck} size={0.65} /> Adesão a protocolos médicos</li>
							<li><Icon path={mdiClipboardPulse} size={0.65} /> Qualidade do atendimento</li>
							<li><Icon path={mdiHospitalBuilding} size={0.65} /> Gestão da clínica/hospital</li>
						</ul>
						<div className={styles.landingCardAction}>
							<span>Acessar</span>
							<Icon path={mdiChevronRight} size={0.8} />
						</div>
					</div>
					)}

					{/* Card Teleconsulta — Paciente */}
					{isPatient && (
					<div className={styles.landingCard} onClick={() => navigate("/medical/teleconsultation/lobby")}>
						<div className={styles.landingCardIcon} style={{ backgroundColor: "rgba(108, 99, 255, 0.1)", color: "#6C63FF" }}>
							<Icon path={mdiVideo} size={2} />
						</div>
						<h2>Teleconsulta (Paciente)</h2>
						<p>Solicite uma consulta por vídeo e aguarde um médico disponível atender.</p>
						<ul className={styles.landingFeatureList}>
							<li><Icon path={mdiVideo} size={0.65} /> Videochamada HD com WebRTC</li>
							<li><Icon path={mdiChatProcessing} size={0.65} /> Chat durante a consulta</li>
							<li><Icon path={mdiClipboardPulse} size={0.65} /> Prontuário em tempo real</li>
							<li><Icon path={mdiAccountHeart} size={0.65} /> Link de acesso para o paciente</li>
						</ul>
						<div className={styles.landingCardAction}>
							<span>Solicitar Consulta</span>
							<Icon path={mdiChevronRight} size={0.8} />
						</div>
					</div>
					)}

					{/* Card Teleconsulta — Médico (Plantão) */}
					{isDoctor && (
					<div className={styles.landingCard} onClick={() => navigate("/medical/teleconsultation/queue")}>
						<div className={styles.landingCardIcon} style={{ backgroundColor: "rgba(0, 59, 113, 0.08)", color: "#003B71" }}>
							<Icon path={mdiStethoscope} size={2} />
						</div>
						<h2>Plantão Teleconsulta (Médico)</h2>
						<p>Fique disponível para atender pacientes por videochamada com transcrição em tempo real.</p>
						<ul className={styles.landingFeatureList}>
							<li><Icon path={mdiVideo} size={0.65} /> Fila de pacientes em tempo real</li>
							<li><Icon path={mdiChatProcessing} size={0.65} /> Transcrição automática da consulta</li>
							<li><Icon path={mdiClipboardPulse} size={0.65} /> Chat durante a chamada</li>
							<li><Icon path={mdiDoctor} size={0.65} /> Aceite pacientes com um clique</li>
						</ul>
						<div className={styles.landingCardAction}>
							<span>Ficar Disponível</span>
							<Icon path={mdiChevronRight} size={0.8} />
						</div>
					</div>
					)}

					{/* Sem perfil configurado */}
					{!profile && (
						<div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "40px 20px", color: "var(--mui-palette-text-secondary)" }}>
							<Icon path={mdiShieldCheck} size={3} color="var(--mui-palette-text-secondary)" style={{ opacity: 0.3 }} />
							<h3>Perfil não configurado</h3>
							<p>Seu perfil médico não foi definido durante o cadastro. Entre em contato com o administrador ou atualize nas configurações.</p>
						</div>
					)}
				</div>

				{/* ── Footer ─────────────────────────── */}
				<footer className={styles.welcomeFooter}>
					<p>Med.IA — Transformando a medicina com inteligência artificial 🚀</p>
				</footer>
			</div>
		</MainLayout>
	);
};

// ── Profile Guard ─────────────────────────────────────────────────

const ProfileGuard: React.FC<{ allowed: MedicalProfileType[]; children: React.ReactNode }> = ({ allowed, children }) => {
	const account = useAccount();
	const profile = (account?.settings?.medicalProfile as MedicalProfileType) || null;
	if (!profile || (!allowed.includes(profile) && profile !== "both")) {
		return (
			<MainLayout>
				<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 16, textAlign: "center", padding: 20 }}>
					<Icon path={mdiShieldCheck} size={3} color="var(--mui-palette-error-main)" style={{ opacity: 0.5 }} />
					<h2>Acesso restrito</h2>
					<p style={{ color: "var(--mui-palette-text-secondary)", maxWidth: 400 }}>
						Você não tem permissão para acessar esta área. Seu perfil está configurado como <strong>{profile === "doctor" ? "Médico" : profile === "patient" ? "Paciente" : "não definido"}</strong>.
					</p>
					<Button variant="contained" onClick={() => navigate("/medical")} sx={{ mt: 1 }}>Voltar ao início</Button>
				</div>
			</MainLayout>
		);
	}
	return <>{children}</>;
};

// ── Router ────────────────────────────────────────────────────────

export const MedicalPage: React.FC = () => {
	return (
		<MobileLayoutProvider>
			<Routes>
				<Route path="/doctor" element={<ProfileGuard allowed={["doctor"]}><MedDoctorPage /></ProfileGuard>} />
				<Route path="/patient" element={<ProfileGuard allowed={["patient"]}><MedPatientPage /></ProfileGuard>} />
				<Route path="/dashboard" element={<ProfileGuard allowed={["doctor"]}><MedDashboardPage /></ProfileGuard>} />
				<Route path="/teleconsultation/:roomId" element={<TeleconsultationPage />} />
				<Route path="*" element={<MedLandingPage />} />
			</Routes>
		</MobileLayoutProvider>
	);
};
